import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/site/page-shell";
import { useWishlist, wishlistStore } from "@/lib/wishlist";
import { cartStore } from "@/lib/cart";
import { toast } from "sonner";
import { Heart, ShoppingCart, Trash2, Package, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/favorites")({
  component: FavoritesPage,
  head: () => ({
    meta: [
      { title: "Favorites — CoderLity Marketplace" },
      { name: "description", content: "Your saved marketplace items — ready when you are." },
      { property: "og:title", content: "Favorites — CoderLity Marketplace" },
      { property: "og:description", content: "Your saved marketplace items — ready when you are." },
      { property: "og:url", content: "/favorites" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/favorites" }],
  }),
});

function formatPrice(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function FavoritesPage() {
  const items = useWishlist();

  const addAllToCart = () => {
    items.forEach((it) => {
      cartStore.add({
        id: it.id,
        name: it.name,
        price: it.price_cents / 100,
        href: `/marketplace/item/${it.slug}`,
      });
    });
    toast.success(`${items.length} item${items.length === 1 ? "" : "s"} added to cart`);
  };

  return (
    <PageShell>
      <section className="mx-auto max-w-6xl px-4 py-10 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
              <Heart className="h-3.5 w-3.5 fill-rose-500 text-rose-500" /> Favorites
            </span>
            <h1 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Your saved items</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {items.length === 0
                ? "You haven't saved anything yet."
                : `${items.length} item${items.length === 1 ? "" : "s"} saved.`}
            </p>
          </div>
          {items.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={addAllToCart}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 transition"
              >
                <ShoppingCart className="h-4 w-4" /> Add all to cart
              </button>
              <button
                type="button"
                onClick={() => {
                  wishlistStore.clear();
                  toast.success("Favorites cleared");
                }}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-muted transition"
              >
                <Trash2 className="h-4 w-4" /> Clear all
              </button>
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-muted">
              <Heart className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="mt-4 text-lg font-bold">No favorites yet</h2>
            <p className="mt-1 text-sm text-muted-foreground">Tap the heart on any marketplace item to save it here.</p>
            <Link to="/marketplace" className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110 transition">
              Browse marketplace
            </Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((it) => (
              <div key={it.id} className="group relative overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg">
                <Link to="/marketplace/item/$slug" params={{ slug: it.slug }}
                  className="block aspect-video overflow-hidden bg-muted">
                  {it.image_url ? (
                    <img src={it.image_url} alt={it.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="grid h-full place-items-center">
                      <Package className="h-8 w-8 text-primary/40" />
                    </div>
                  )}
                </Link>
                <div className="p-4">
                  <Link to="/marketplace/item/$slug" params={{ slug: it.slug }}
                    className="block text-sm font-bold leading-tight line-clamp-2 hover:text-primary transition">
                    {it.name}
                  </Link>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm font-black text-primary">
                      {formatPrice(it.price_cents, it.currency)}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        wishlistStore.remove(it.id);
                        toast.success("Removed from favorites");
                      }}
                      aria-label="Remove"
                      className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      cartStore.add({
                        id: it.id,
                        name: it.name,
                        price: it.price_cents / 100,
                        href: `/marketplace/item/${it.slug}`,
                      });
                      toast.success(`${it.name} added to cart`);
                    }}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:brightness-110 transition"
                  >
                    <ShoppingCart className="h-3.5 w-3.5" /> Add to cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10">
          <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to marketplace
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
